<template>
  <div class="d-flex flex-column w-100">
    <div
      v-show="(video && !video.isVideoAvailable) || isError"
      class="text-center w-100 pt-4 pb-4 bg-light rounded mb-2"
    >
      <div class="text-black-50">
        <div v-if="video && !video.isVideoAvailable">
          {{ unAvailableVideoMessage }}
        </div>

        <div v-if="isError">
          {{ isReloading ? 'Reloading video' : 'Video failed to display' }}
        </div>
      </div>

      <b-button v-if="isError" variant="link" class="pb-0" @click.prevent.stop="reload">
        <b-spinner v-if="isReloading" variant="dark" small />

        <span v-else>
          <font-awesome-icon
            :icon="{ prefix: 'fas', iconName: 'rotate-right' }"
            class="icon__rotate-right"
          />

          Reload
        </span>
      </b-button>
    </div>

    <vueVimeoPlayer
      v-if="!isError && video && video.isVideoAvailable"
      ref="player"
      class="player__vimeo"
      :video-id="`${video.video_id}`"
      :video-url="video.video_link"
      :autoplay="autoplay"
      :player-width="width"
      :player-height="height"
      :options="options"
      @ready="onVideoReady"
      @error="onVideoError"
    />
  </div>
</template>

<script>
import Vimeo from '@/models/Vimeo';
import { vueVimeoPlayer } from 'vue-vimeo-player';

export default {
  name: 'BaseVideoPlayer',

  components: {
    vueVimeoPlayer
  },

  props: {
    video: {
      type: Object,
      default: () => {}
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 300
    },
    options: {
      type: Object,
      default: () => {
        return {
          responsive: true
        };
      }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isError: false,
      isReloading: false
    };
  },

  computed: {
    unAvailableVideoMessage() {
      // const unAvailableStatuses = [
      //   'quota_exceeded',
      //   'total_cap_exceeded',
      //   'transcoding_error',
      //   'uploading_error',
      //   'unavailable'
      // ];

      const processingStatuses = ['transcode_starting', 'transcoding', 'uploading'];

      const { status } = this.video;

      let message = status === 'available' ? '' : 'Video is not available';

      if (status !== 'available') {
        if (processingStatuses.includes(status)) message = 'Video is processing';
      }

      return message;
    }
  },

  methods: {
    async reload() {
      try {
        this.isReloading = true;

        if (this.video) {
          const { response } = await Vimeo.api().getVideoInformation(this.video.id);

          const { data } = response;

          this.$emit('onVideoReload', data);
        }

        this.isError = false;
      } catch (error) {
        this.isError = true;

        if (this.$sentry) {
          this.$sentry.captureException(error);
        }
      } finally {
        this.isReloading = false;
      }
    },

    onVideoReady() {
      this.$emit('onVideoReady', true);
    },

    onVideoError(error, player) {
      this.isError = true;

      console.log('[BaseVideoPlayer] video error.', { error, player });

      if (this.$sentry) {
        this.$sentry.withScope((scope) => {
          scope.setExtra('state', { error, player });

          this.$sentry.captureException(error);
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.player__vimeo {
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  position: relative;
}

::v-deep iframe {
  border-radius: 7px;
}
</style>
